<template>
    <div class="home mt-5">
        <b-container class="list-retirement-factors-wrapper">
            <b-card no-body class="mt-3 shadow-sm">
                <b-row class="p-2">
                    <b-col lg="12" class="my-1">
                        <input type="file" @change="importExcel" accept=".xlsx,.xls,.xlsm">
                        <button class="bg-transparent w-100 text-center border border-dashed pb-10 text-gray-600"
                            @click="saveRetirementFactors" :disabled="processing">
                            Importar Fatores Previdenciários
                        </button>
                        <div class="text-center text-default my-2" v-if="errorText">
                            {{ errorText }}
                        </div>
                        <div class="text-center text-default my-2" v-if="processing">
                            <b-spinner class="align-middle"></b-spinner> Importando...
                        </div>
                    </b-col>
                </b-row>
            </b-card>

            <b-card no-body class="mt-3 shadow-sm">
                <b-table :items="items" :fields="fields" :current-page="currentPage" :per-page="perPage"
                    :filter="filter" :filter-included-fields="filterOn" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" :sort-direction="sortDirection" :busy="isLoading" stacked="md" show-empty
                    small @filtered="onFiltered">
                    <template #table-busy>
                        <div class="text-center text-default my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong> Carregando...</strong>
                        </div>
                    </template>
                </b-table>
            </b-card>

            <div class="d-flex justify-content-center mt-3">
                <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" size="sm"
                    class="my-0"></b-pagination>
            </div>
        </b-container>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { getRetirementFactors, createRetirementFactors } from '../../services/retirementFactors';

export default {
    name: 'RetirementFactors',
    data() {
        return {
            isLoading: false,
            items: [],
            fields: [
                {
                    key: 'contributionTime',
                    label: 'Tempo de Contribuição',
                    align: 'center',
                    sortable: true,
                    sortDirection: 'desc',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                    formatter: (value) => `${value} anos`
                },
                {
                    key: 'lifeExpectancy',
                    label: 'Expectativa de Vida',
                    sortable: true,
                    sortDirection: 'desc',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                    formatter: (value) => `${value} anos`
                },
                {
                    key: 'factor',
                    label: 'Fator',
                    sortable: true,
                    sortDirection: 'desc',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                    formatter: (value) => value.toFixed(4)
                }
            ],
            totalRows: 1,
            currentPage: 1,
            perPage: 20,
            pageOptions: [5, 10, 15, 50, 100],
            sortBy: '',
            sortDesc: false,
            sortDirection: 'asc',
            filter: null,
            filterOn: [],
            file: null,
            processing: null,
            errorText: "",
        }
    },
    computed: {
        sessionUser() { return this.$store.getters.sessionUser },
        sortOptions() {
            // Create an options list from our fields
            return this.fields
                .filter(f => f.sortable)
                .map(f => {
                    return { text: f.label, value: f.key }
                })
        }
    },
    mounted() {
        // Set the initial number of items
        this.totalRows = this.items.length
        this.load();
    },
    methods: {
        async load() {
            this.isLoading = true;

            try {
                const data = await getRetirementFactors();
                this.items = data || [];
                this.totalRows = this.items.length;
            } catch (error) {
                console.error('Error loading retirement factors:', error);
                this.$toast.open({
                    message: 'Erro ao carregar fatores previdenciários',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                this.isLoading = false;
            }
        },
        onFiltered(filteredItems) {
            // Trigger pagination to update the number of buttons/pages due to filtering
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },
        async importExcel(e) {
            this.errorText = "";

            if (e.target.files[0]) {
                this.file = e.target.files[0];
                return;
            }

            this.file = null;
        },
        async saveRetirementFactors() {
            if (this.file) {
                try {
                    this.processing = true;

                    const data = await this.readExcelFile(this.file);
                    const retirementFactors = this.parseExcelData(data);

                    if (retirementFactors.length === 0) {
                        throw new Error('Nenhum dado válido encontrado no arquivo');
                    }

                    await createRetirementFactors({ retirementFactors });

                    this.$toast.open({
                        message: 'Fatores previdenciários importados com sucesso!',
                        type: 'success',
                        position: 'top-right'
                    });

                    await this.load();

                } catch (error) {
                    console.error('Error importing Excel:', error);
                    this.$toast.open({
                        message: error.message || 'Erro ao importar arquivo Excel',
                        type: 'error',
                        position: 'top-right'
                    });
                } finally {
                    this.processing = false;
                }
            }
        },

        readExcelFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target.result)
                        const workbook = XLSX.read(data, { type: 'array' })
                        const sheetName = workbook.SheetNames[0]
                        const worksheet = workbook.Sheets[sheetName]
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true })
                        resolve(jsonData)
                    } catch (error) {
                        reject(new Error('Erro ao ler arquivo Excel'))
                    }
                }
                reader.onerror = () => reject(new Error('Erro ao ler arquivo'))
                reader.readAsArrayBuffer(file)
            })
        },

        parseExcelData(data) {
            // Fixed positions (1-based from your description)
            const LIFE_ROW_1BASED = 5;     // row with 43, 44, 45, ...
            const DATA_START_1BASED = 6;   // first data row
            const CT_COL_1BASED = 3;       // column C
            const FACTOR_START_COL_1BASED = 4; // column D

            // Convert to zero-based
            const LIFE_ROW = LIFE_ROW_1BASED - 1;
            const DATA_START = DATA_START_1BASED - 1;
            const CT_COL = CT_COL_1BASED - 1;
            const FACTOR_START_COL = FACTOR_START_COL_1BASED - 1;

            const isPresent = v => v !== null && v !== undefined && String(v).trim() !== '';
            const isRowEmpty = row => !row || !row.some(isPresent);
            const parseLocaleNumber = (x) => {
                if (typeof x === 'number') return x;
                if (x == null) return NaN;
                let s = String(x).trim();
                // handle "1.234,56" and "0,311"
                if (s.includes(',') && !s.includes('.')) {
                    s = s.replace(/\./g, ''); // thousands
                    s = s.replace(',', '.');  // decimal
                }
                return parseFloat(s);
            };

            // Guard
            if (!Array.isArray(data) || data.length <= LIFE_ROW) return [];

            // Header row with lifeExpectancy values (starts at column D)
            const lifeRow = data[LIFE_ROW] || [];
            const lifeExpectancies = [];
            for (let c = FACTOR_START_COL; c < lifeRow.length; c++) {
                const v = parseLocaleNumber(lifeRow[c]);
                if (Number.isFinite(v)) {
                    lifeExpectancies.push({ col: c, life: Math.trunc(v) });
                } else {
                    // stop if/when header stops being numeric
                    // (remove this break if there are gaps later in the header)
                    break;
                }
            }
            if (lifeExpectancies.length === 0) return [];

            const out = [];

            // Data rows: from row 6 downward
            for (let r = DATA_START; r < data.length; r++) {
                const row = data[r];
                if (isRowEmpty(row)) continue;

                // contributionTime is in column C
                const ctNum = parseLocaleNumber(row[CT_COL]);
                if (!Number.isFinite(ctNum)) {
                    // if CT is blank, it's likely the end of the table block
                    continue;
                }
                const contributionTime = Math.trunc(ctNum);

                // factors across columns aligned to lifeExpectancy header
                for (const { col, life } of lifeExpectancies) {
                    const fac = parseLocaleNumber(row[col]);
                    if (Number.isFinite(fac)) {
                        out.push({
                            contributionTime,      // from column C
                            lifeExpectancy: life,  // from row 5 (ages)
                            factor: Number(fac)    // matrix value
                        });
                    }
                }
            }

            return out;
        }



    }
}
</script>

<style lang="scss">
.list-retirement-factors-wrapper {
    .table.b-table {
        margin-bottom: 0;

        &>thead>tr th {
            border: none;
        }

        td,
        th {
            vertical-align: middle;
            display: table-cell;
            padding: 1rem 1.5rem;
        }
    }
}
</style>